import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { updateInventoryRecord } from './functions/get_inventory.functions';

const EditInventoryModal = ({ open, onClose, selectedRecord, onUpdateSuccess, network }) => {
  const [formData, setFormData] = useState({
    fiber_type: '',
    fiber_tenancy_type: '',
    vendor: '',
    fiber_pair: '',
    used_pair: '',
    commission_date: '',
    link_rate: '',
  });
  const [errors, setErrors] = useState({});
  const [originalTenancyType, setOriginalTenancyType] = useState('');
  const [originalFieldNames, setOriginalFieldNames] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Normalize tenancy value (similar to table component)
  const normalizeTenancyForDisplay = (value) => {
    if (!value) {
      return '';
    }
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'own') {
      return 'Owned';
    }
    if (normalized === 'owned') {
      return 'Owned';
    }
    if (normalized === 'iru') {
      return 'Leased';
    }
    if (normalized === 'leased') {
      return 'Leased';
    }
    // Capitalize first letter for other values
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  useEffect(() => {
    if (selectedRecord) {
      const rawData = selectedRecord.raw || selectedRecord;

      // Determine which field names exist in the backend response
      // Check if field exists in object (using 'in' operator to handle null values)
      const fieldMapping = {
        fiber_type: 'fiber_type' in rawData ? 'fiber_type' : null,
        tenancy:
          'fiber_tenancy_type' in rawData
            ? 'fiber_tenancy_type'
            : 'tenancy_type' in rawData
              ? 'tenancy_type'
              : null,
        vendor: 'vendor' in rawData ? 'vendor' : null,
        fiber_pair:
          'fiber_pair' in rawData ? 'fiber_pair' : 'pair_count' in rawData ? 'pair_count' : null,
        used_pair: 'used_pair' in rawData ? 'used_pair' : null,
        commission_date:
          'commission_date' in rawData
            ? 'commission_date'
            : 'commissioned_at' in rawData
              ? 'commissioned_at'
              : null,
        link_rate:
          'link_rate_gbps' in rawData
            ? 'link_rate_gbps'
            : 'link_rate' in rawData
              ? 'link_rate'
              : null,
      };

      // Store original field names mapping
      setOriginalFieldNames(fieldMapping);

      const backendTenancyType = rawData.fiber_tenancy_type || rawData.tenancy_type || '';
      // Store original tenancy type to check if it was IRU
      setOriginalTenancyType(backendTenancyType);

      // Normalize tenancy type for display (Own -> Owned, IRU -> Leased)
      const displayTenancyType = normalizeTenancyForDisplay(backendTenancyType);

      setFormData({
        fiber_type: rawData.fiber_type || '',
        fiber_tenancy_type: displayTenancyType || '',
        vendor: rawData.vendor || '',
        fiber_pair: rawData.fiber_pair || rawData.pair_count || '',
        used_pair: rawData.used_pair || '',
        commission_date: rawData.commission_date || rawData.commissioned_at || '',
        link_rate: rawData.link_rate_gbps || rawData.link_rate || '',
      });
      setErrors({});
    }
  }, [selectedRecord]);

  const handleClose = () => {
    // Prevent closing while update is in progress
    if (isUpdating) {
      return;
    }
    setFormData({
      fiber_type: '',
      fiber_tenancy_type: '',
      vendor: '',
      fiber_pair: '',
      used_pair: '',
      commission_date: '',
      link_rate: '',
    });
    setErrors({});
    setOriginalTenancyType('');
    setOriginalFieldNames({});
    setIsUpdating(false);
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate fiber_pair and used_pair as numbers
    if (formData.fiber_pair && isNaN(Number(formData.fiber_pair))) {
      newErrors.fiber_pair = 'Fiber Pair must be a number';
    }
    if (formData.used_pair && isNaN(Number(formData.used_pair))) {
      newErrors.used_pair = 'Used Pair must be a number';
    }

    // Validate commission_date format (optional but should be valid if provided)
    if (formData.commission_date) {
      const date = new Date(formData.commission_date);
      if (isNaN(date.getTime())) {
        newErrors.commission_date = 'Invalid date format';
      }
    }

    // Validate link_rate as number
    if (formData.link_rate && isNaN(Number(formData.link_rate))) {
      newErrors.link_rate = 'Link Rate must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    // Format as YYYY-MM-DD for date input
    return date.toISOString().split('T')[0];
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedRecord) {
      console.error('No record selected for update');
      return;
    }

    const rawData = selectedRecord.raw || selectedRecord;
    // Try multiple possible ID field names
    const recordId =
      rawData.id ||
      rawData._id ||
      rawData.link_id ||
      rawData.inventory_id ||
      rawData.record_id ||
      rawData.uuid;

    if (!recordId) {
      console.error('No ID found for the record. Available fields:', Object.keys(rawData));
      alert('Unable to identify record for update. Please contact support.');
      return;
    }

    if (!network) {
      console.error('Network is required for updating inventory record');
      return;
    }

    setIsUpdating(true);

    try {
      // Handle tenancy type: normalize for backend
      let tenancyTypeToSend = formData.fiber_tenancy_type || null;
      const originalNormalized = originalTenancyType
        ? String(originalTenancyType).trim().toLowerCase()
        : '';

      // If original was IRU and user selects Leased, send IRU back
      if (originalNormalized === 'iru' && formData.fiber_tenancy_type === 'Leased') {
        tenancyTypeToSend = 'IRU';
      }
      // If original was Own/Owned and user selects Owned, send back the original format
      else if (
        (originalNormalized === 'own' || originalNormalized === 'owned') &&
        formData.fiber_tenancy_type === 'Owned'
      ) {
        // Keep original format (could be "Own" or "Owned")
        tenancyTypeToSend = originalTenancyType;
      }
      // If user changes the value, send what they selected
      else if (tenancyTypeToSend) {
        // If user selected "Owned" but original wasn't Own/Owned, check if we should send "Own" or "Owned"
        // For now, send as selected
      }

      // Prepare update payload using only the exact field names from backend
      const updatePayload = {};

      // Only include fields that existed in the original backend response
      if (originalFieldNames.fiber_type && formData.fiber_type) {
        updatePayload[originalFieldNames.fiber_type] = formData.fiber_type;
      }

      if (originalFieldNames.tenancy && tenancyTypeToSend) {
        updatePayload[originalFieldNames.tenancy] = tenancyTypeToSend;
      }

      if (originalFieldNames.vendor && formData.vendor) {
        updatePayload[originalFieldNames.vendor] = formData.vendor;
      }

      if (originalFieldNames.fiber_pair && formData.fiber_pair) {
        updatePayload[originalFieldNames.fiber_pair] = Number(formData.fiber_pair);
      }

      if (originalFieldNames.used_pair && formData.used_pair !== '') {
        updatePayload[originalFieldNames.used_pair] = Number(formData.used_pair);
      }

      if (originalFieldNames.commission_date && formData.commission_date) {
        updatePayload[originalFieldNames.commission_date] = formData.commission_date;
      }

      if (originalFieldNames.link_rate && formData.link_rate) {
        updatePayload[originalFieldNames.link_rate] = Number(formData.link_rate);
      }

      // Make the API call
      const result = await updateInventoryRecord(recordId, updatePayload, network);

      // Update row data after API completes
      if (onUpdateSuccess && result?.response) {
        onUpdateSuccess(result.response, recordId);
      }

      setIsUpdating(false);

      // Close modal
      handleClose();
    } catch (error) {
      console.error('Error updating inventory record:', error);
      setIsUpdating(false);
      // You can add toast notification here if needed
    }
  };

  if (!selectedRecord) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={isUpdating ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isUpdating}
    >
      <DialogTitle>
        <Typography variant="h6" style={{ color: '#1C274C', fontWeight: 600 }}>
          Edit Inventory Record
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fiber Type"
                value={formData.fiber_type}
                onChange={(e) => handleChange('fiber_type', e.target.value)}
                error={!!errors.fiber_type}
                helperText={errors.fiber_type}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tenancy Type</InputLabel>
                <Select
                  value={formData.fiber_tenancy_type}
                  label="Tenancy Type"
                  onChange={(e) => handleChange('fiber_tenancy_type', e.target.value)}
                >
                  <MenuItem value="Owned">Owned</MenuItem>
                  <MenuItem value="Leased">Leased</MenuItem>
                  <MenuItem value="RoW">RoW</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vendor"
                value={formData.vendor}
                onChange={(e) => handleChange('vendor', e.target.value)}
                error={!!errors.vendor}
                helperText={errors.vendor}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fiber Pair"
                type="number"
                value={formData.fiber_pair}
                onChange={(e) => handleChange('fiber_pair', e.target.value)}
                error={!!errors.fiber_pair}
                helperText={errors.fiber_pair}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Used Pair"
                type="number"
                value={formData.used_pair}
                onChange={(e) => handleChange('used_pair', e.target.value)}
                error={!!errors.used_pair}
                helperText={errors.used_pair}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission Date"
                type="date"
                value={formatDateForInput(formData.commission_date)}
                onChange={(e) => handleChange('commission_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors.commission_date}
                helperText={errors.commission_date}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Link Rate (Gbps)"
                type="number"
                value={formData.link_rate}
                onChange={(e) => handleChange('link_rate', e.target.value)}
                error={!!errors.link_rate}
                helperText={errors.link_rate}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          style={{ backgroundColor: '#BD1D23' }}
          disabled={isUpdating}
          startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInventoryModal;
